import { Image, StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";

import { BoldText } from "../BoldText";
import { CourseIcon } from "../svg/CourseIcon";

import { courseTheme } from "@/lib/training/courseTheme";
import type { Block } from "@/lib/training/types";

// Renders one content Block. Ports the markup from CPR_Course_Module1.html
// content cards (.twocol, .statbox, .chain, .pullquote, .callout, body-p).

export function BlockRenderer({ block }: { block: Block }) {
    switch (block.kind) {
        case "twoCol":
            return (
                <View style={styles.twoCol}>
                    {block.cards.map((c, i) => (
                        <View key={i} style={styles.mini}>
                            <View style={styles.miniIcon}>
                                <CourseIcon id={c.icon} size={20} color={courseTheme.tealD} />
                            </View>
                            <Text style={styles.miniTitle}>{c.title}</Text>
                            <BoldText
                                text={c.body}
                                style={styles.miniBody}
                                boldColor={courseTheme.navy}
                            />
                        </View>
                    ))}
                </View>
            );
        case "pullquote":
            return (
                <View style={styles.pullquote}>
                    <Text style={styles.pullquoteText}>{block.text}</Text>
                </View>
            );
        case "callout":
            return <Callout tone={block.tone} text={block.text} />;
        case "bodyP":
            return <BoldText text={block.text} style={styles.bodyP} boldColor={courseTheme.navy} />;
        case "chips":
            return (
                <View style={styles.chips}>
                    {block.items.map((c, i) => (
                        <View key={i} style={styles.chip}>
                            <Text style={styles.chipLabel}>{c.label}</Text>
                            {c.sub ? <Text style={styles.chipSub}>{c.sub}</Text> : null}
                        </View>
                    ))}
                </View>
            );
        case "numbered":
            return (
                <View style={styles.numbered}>
                    {block.items.map((t, i) => (
                        <View key={i} style={styles.numRow}>
                            <View style={styles.numN}>
                                <Text style={styles.numNText}>{i + 1}</Text>
                            </View>
                            <BoldText
                                text={t}
                                style={styles.numText}
                                boldColor={courseTheme.navy}
                            />
                        </View>
                    ))}
                </View>
            );
        case "doDont":
            return (
                <View style={styles.doDont}>
                    <View style={[styles.ddCard, styles.ddDo]}>
                        <Text style={styles.ddDoLabel}>{block.doLabel}</Text>
                        <Text style={styles.ddText}>{block.doText}</Text>
                    </View>
                    <View style={[styles.ddCard, styles.ddDont]}>
                        <Text style={styles.ddDontLabel}>{block.dontLabel}</Text>
                        <Text style={styles.ddText}>{block.dontText}</Text>
                    </View>
                </View>
            );
        case "image":
            return (
                <View style={styles.imageBlock}>
                    <View
                        style={[
                            styles.imageFrame,
                            { aspectRatio: block.aspect === "3:4" ? 3 / 4 : 4 / 3 }
                        ]}
                    >
                        <Image
                            source={{ uri: block.image }}
                            style={StyleSheet.absoluteFill}
                            resizeMode="cover"
                        />
                    </View>
                    <View style={styles.imageCap}>
                        <Text style={styles.imageLbl}>{block.label}</Text>
                        <Text style={styles.imageSub}>{block.sub}</Text>
                    </View>
                </View>
            );
        case "imageRow":
            return (
                <View style={styles.imageRow}>
                    {block.images.map((im, i) => (
                        <View key={i} style={styles.imageRowItem}>
                            <View style={[styles.imageFrame, { aspectRatio: 3 / 4 }]}>
                                <Image
                                    source={{ uri: im.image }}
                                    style={StyleSheet.absoluteFill}
                                    resizeMode="cover"
                                />
                            </View>
                            <View style={styles.imageCap}>
                                <Text style={styles.imageLbl}>{im.label}</Text>
                                <Text style={styles.imageSub}>{im.sub}</Text>
                            </View>
                        </View>
                    ))}
                </View>
            );
        default: {
            const _exhaustive: never = block;
            return _exhaustive;
        }
    }
}

export function Callout({ tone, text }: { tone: "amber" | "tip"; text: string }) {
    const amber = tone === "amber";
    return (
        <View
            style={[
                styles.callout,
                { backgroundColor: amber ? courseTheme.amberTint : courseTheme.tealTint }
            ]}
        >
            <CourseIcon
                id={amber ? "alert" : "bulb"}
                size={20}
                color={amber ? courseTheme.amber : courseTheme.tealD}
            />
            <BoldText text={text} style={styles.calloutText} boldColor={courseTheme.navy} />
        </View>
    );
}

const styles = StyleSheet.create({
    twoCol: { flexDirection: "row", gap: 12, marginTop: 16 },
    mini: {
        flex: 1,
        borderWidth: 1,
        borderColor: courseTheme.line,
        borderRadius: 16,
        padding: 14,
        backgroundColor: courseTheme.white
    },
    miniIcon: {
        width: 36,
        height: 36,
        borderRadius: 11,
        backgroundColor: courseTheme.tealTint,
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 10
    },
    miniTitle: { fontSize: 15, fontWeight: "800", color: courseTheme.navy, marginBottom: 4 },
    miniBody: { fontSize: 13, lineHeight: 19, color: courseTheme.slate },
    statbox: { borderRadius: 20, padding: 22, marginTop: 16, alignItems: "center" },
    statBig: { fontSize: 40, fontWeight: "800", color: "#FFFFFF" },
    statCap: { fontSize: 13, color: "#CFE0EA", textAlign: "center", marginTop: 4 },
    statSplit: {
        flexDirection: "row",
        gap: 14,
        marginTop: 18,
        borderTopWidth: 1,
        borderTopColor: "rgba(255,255,255,0.14)",
        paddingTop: 16
    },
    statSplitItem: { flex: 1, alignItems: "center" },
    statSplitVal: { fontSize: 18, fontWeight: "800", color: courseTheme.tealL },
    statSplitLabel: { fontSize: 12, color: "#CFE0EA", textAlign: "center", marginTop: 2 },
    chain: { marginTop: 16, position: "relative" },
    chainLine: {
        position: "absolute",
        left: 21,
        top: 8,
        bottom: 52,
        width: 2,
        backgroundColor: courseTheme.tealTint2
    },
    link: { flexDirection: "row", gap: 13, alignItems: "flex-start", marginBottom: 12 },
    linkN: {
        width: 44,
        height: 44,
        borderRadius: 13,
        borderWidth: 2,
        backgroundColor: courseTheme.white,
        alignItems: "center",
        justifyContent: "center"
    },
    linkNMine: { borderColor: courseTheme.teal },
    linkNMuted: { borderColor: courseTheme.line },
    linkBody: { flex: 1, paddingTop: 4 },
    linkTitle: { fontSize: 14.5, fontWeight: "800", color: courseTheme.navy },
    linkSub: { fontSize: 13, lineHeight: 18, color: courseTheme.slate, marginTop: 2 },
    badge: {
        alignSelf: "flex-start",
        backgroundColor: courseTheme.tealTint,
        borderRadius: 99,
        paddingHorizontal: 13,
        paddingVertical: 7,
        marginTop: 2
    },
    badgeText: { fontSize: 12.5, fontWeight: "700", color: courseTheme.tealD },
    pullquote: {
        borderLeftWidth: 3,
        borderLeftColor: courseTheme.teal,
        paddingLeft: 14,
        marginTop: 16
    },
    pullquoteText: { fontSize: 16, fontStyle: "italic", color: courseTheme.navy, lineHeight: 23 },
    callout: {
        flexDirection: "row",
        gap: 11,
        borderRadius: 16,
        padding: 15,
        marginTop: 16,
        alignItems: "flex-start"
    },
    calloutText: { flex: 1, fontSize: 13.5, lineHeight: 20, color: courseTheme.slate },
    bodyP: { fontSize: 14.5, lineHeight: 22, color: courseTheme.slate, marginTop: 14 },
    imageBlock: { marginTop: 16 },
    imageFrame: {
        width: "100%",
        borderRadius: 16,
        borderWidth: 1,
        borderColor: courseTheme.line,
        backgroundColor: courseTheme.paper,
        overflow: "hidden"
    },
    imageCap: { marginTop: 8 },
    imageLbl: { fontSize: 13, fontWeight: "800", color: courseTheme.navy },
    imageSub: { fontSize: 11.5, fontWeight: "600", color: courseTheme.slate, marginTop: 1 },
    imageRow: { flexDirection: "row", gap: 12, marginTop: 16 },
    imageRowItem: { flex: 1 },
    chips: { flexDirection: "row", flexWrap: "wrap", gap: 8, marginTop: 16 },
    chip: {
        borderWidth: 1,
        borderColor: courseTheme.tealTint2,
        backgroundColor: courseTheme.tealTint,
        borderRadius: 12,
        paddingHorizontal: 13,
        paddingVertical: 9
    },
    chipLabel: { fontSize: 13, fontWeight: "800", color: courseTheme.navy },
    chipSub: { fontSize: 11, fontWeight: "600", color: courseTheme.slate, marginTop: 1 },
    numbered: { marginTop: 16, gap: 10 },
    numRow: { flexDirection: "row", gap: 11, alignItems: "flex-start" },
    numN: {
        width: 24,
        height: 24,
        borderRadius: 8,
        backgroundColor: courseTheme.teal,
        alignItems: "center",
        justifyContent: "center"
    },
    numNText: { fontSize: 12, fontWeight: "800", color: "#FFFFFF" },
    numText: { flex: 1, fontSize: 14, lineHeight: 20, color: courseTheme.slate, paddingTop: 2 },
    doDont: { flexDirection: "row", gap: 12, marginTop: 16 },
    ddCard: { flex: 1, borderRadius: 16, padding: 14, borderWidth: 1 },
    ddDo: { backgroundColor: courseTheme.greenTint, borderColor: "#BFE8CD" },
    ddDont: { backgroundColor: courseTheme.redTint, borderColor: "#F4CFD0" },
    ddDoLabel: { fontSize: 13, fontWeight: "800", color: courseTheme.green, marginBottom: 4 },
    ddDontLabel: { fontSize: 13, fontWeight: "800", color: courseTheme.red, marginBottom: 4 },
    ddText: { fontSize: 13, lineHeight: 18, color: courseTheme.slate }
});
